<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useMutation, useQuery } from "@tanstack/vue-query";
import {
  UiButton,
  UiCard,
  UiIcon,
  UiInfoBox,
  UiText,
  useApi,
} from "@shopexperts/shared";
import {
  formatCheckoutAmount,
  formatCheckoutCadence,
  formatCheckoutDate,
  getPendingCheckoutState,
  mapBillingRedemptionError,
  type BillingCheckoutReturnState,
  type BillingRedemptionErrorDisplay,
  type ExpertCheckoutSummary,
  type ExpertInvitationOfferPreview,
  type ExpertOfferTerms,
} from "@/app/composables/expert-billing/expertCheckout";

const props = defineProps<{
  enabled: boolean;
  isExpertRole: boolean;
  invitationToken: string | null;
  returnState: BillingCheckoutReturnState;
  paymentConfirmed: boolean;
  confirmationTimedOut: boolean;
}>();

interface ExpertBillingCheckoutApi {
  previewInvitation: (args: {
    body: { token: string };
  }) => Promise<ExpertInvitationOfferPreview>;
  getPendingCheckout: () => Promise<{
    pending_checkout: ExpertCheckoutSummary | null;
  }>;
  redeemInvitation: (args: {
    body: { token: string };
  }) => Promise<ExpertCheckoutSummary>;
}

type CheckoutCardMode =
  | "hidden"
  | "role"
  | "success"
  | "confirmed"
  | "timeout"
  | "redirecting"
  | "error"
  | "resume"
  | "expired"
  | "unavailable"
  | "invite"
  | "preview-loading"
  | "cancelled-loading"
  | "cancelled-empty"
  | "pending-error";

const api = useApi();
// The dashboard typechecker resolves the ignored legacy-api dist declarations,
// while Vite resolves workspace source. Keep this narrow bridge local until
// the package is published; runtime still uses the schema-validated API client.
const expertBillingApi = (api.expert as typeof api.expert & {
  billing: ExpertBillingCheckoutApi;
}).billing;
const redeemedCheckout = ref<ExpertCheckoutSummary | null>(null);
const redemptionError = ref<BillingRedemptionErrorDisplay | null>(null);
const isRedirecting = ref(false);
const showRedirectFallback = ref(false);
const redirectFallbackUrl = ref<string | null>(null);
let redirectFallbackTimer: ReturnType<typeof setTimeout> | null = null;

const pendingQuery = useQuery({
  queryKey: ["expert", "billing", "pending-checkout"],
  enabled: computed(() => props.enabled && props.isExpertRole),
  queryFn: () => expertBillingApi.getPendingCheckout(),
  staleTime: 0,
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
});

const previewQuery = useQuery({
  queryKey: ["expert", "billing", "invitation-preview"],
  enabled: computed(() => props.enabled && props.isExpertRole && Boolean(props.invitationToken)),
  queryFn: () => expertBillingApi.previewInvitation({
    body: { token: props.invitationToken as string },
  }),
  staleTime: 0,
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
});

const pendingCheckout = computed(() => pendingQuery.data.value?.pending_checkout ?? null);
const pendingState = computed(() => getPendingCheckoutState(pendingCheckout.value));
const previewError = computed(() => (
  previewQuery.error.value ? mapBillingRedemptionError(previewQuery.error.value) : null
));
const activeError = computed(() => redemptionError.value ?? previewError.value);

const cardMode = computed<CheckoutCardMode>(() => {
  if (!props.enabled) return "hidden";
  if (!props.isExpertRole) return "role";
  if (props.returnState.kind === "success") {
    if (props.paymentConfirmed) return "confirmed";
    return props.confirmationTimedOut ? "timeout" : "success";
  }
  if (redemptionError.value) return "error";
  if (isRedirecting.value) return "redirecting";
  if (props.invitationToken) {
    if (previewQuery.isLoading.value) return "preview-loading";
    if (previewError.value) return "error";
    if (previewQuery.data.value) return "invite";
  }
  if (pendingState.value.kind === "resumable") return "resume";
  if (pendingState.value.kind === "expired") return "expired";
  if (pendingState.value.kind === "unavailable") return "unavailable";
  if (props.returnState.kind === "cancelled" && pendingQuery.isLoading.value) return "cancelled-loading";
  if (props.returnState.kind === "cancelled" && pendingQuery.isError.value) return "pending-error";
  if (props.returnState.kind === "cancelled") return "cancelled-empty";
  return "hidden";
});

const offerTerms = computed<ExpertOfferTerms | null>(() => {
  if (redeemedCheckout.value) return redeemedCheckout.value;
  if (cardMode.value === "invite" && previewQuery.data.value) return previewQuery.data.value;
  return pendingState.value.kind === "none" ? null : pendingState.value.checkout;
});
const offerExpiresAt = computed(() => {
  if (cardMode.value === "invite") return previewQuery.data.value?.invitation.expires_at ?? null;
  if (pendingState.value.kind === "none") return null;
  return pendingState.value.checkout.checkout_expires_at;
});

const isBusy = computed(() => (
  redeemMutation.isPending.value
  || pendingQuery.isFetching.value
  || previewQuery.isFetching.value
));
const canRetryNow = computed(() => {
  const retryAt = activeError.value?.retryAt;
  if (!retryAt) return true;
  const timestamp = new Date(retryAt).getTime();
  return !Number.isFinite(timestamp) || timestamp <= Date.now();
});
const errorActionLabel = computed(() => (
  activeError.value?.action === "refresh" ? "Find open checkout" : "Try again"
));

function redirectToCheckout(checkoutUrl: string) {
  isRedirecting.value = true;
  showRedirectFallback.value = false;
  redirectFallbackUrl.value = null;
  if (redirectFallbackTimer) clearTimeout(redirectFallbackTimer);
  redirectFallbackTimer = null;

  try {
    const target = new URL(checkoutUrl);
    if (target.protocol !== "https:") throw new Error("Checkout must use HTTPS");
    redirectFallbackUrl.value = target.toString();
    window.location.replace(target.toString());
    redirectFallbackTimer = setTimeout(() => {
      showRedirectFallback.value = true;
      redirectFallbackTimer = null;
    }, 1500);
  } catch {
    showRedirectFallback.value = true;
  }
}

const redeemMutation = useMutation({
  mutationFn: (token: string) => expertBillingApi.redeemInvitation({ body: { token } }),
  onSuccess: (checkout) => {
    redeemedCheckout.value = checkout;
    redemptionError.value = null;
    if (checkout.checkout_url) {
      redirectToCheckout(checkout.checkout_url);
      return;
    }
    isRedirecting.value = false;
    redemptionError.value = {
      kind: "retryable",
      title: "Checkout link is not ready",
      message: "The offer was found, but Stripe did not return a usable checkout link. Try again.",
      action: "retry",
      retryAt: null,
    };
  },
  onError: (error) => {
    isRedirecting.value = false;
    redemptionError.value = mapBillingRedemptionError(error);
  },
});

async function startCheckout() {
  if (!props.invitationToken || redeemMutation.isPending.value) return;
  redemptionError.value = null;
  try {
    await redeemMutation.mutateAsync(props.invitationToken);
  } catch {
    // The mutation maps the stable backend error into the inline state.
  }
}

function resumeCheckout() {
  if (pendingState.value.kind !== "resumable") return;
  redeemedCheckout.value = pendingState.value.checkout;
  redirectToCheckout(pendingState.value.checkout.checkout_url as string);
}

async function refreshPendingCheckout() {
  redemptionError.value = null;
  const result = await pendingQuery.refetch();
  const state = getPendingCheckoutState(result.data?.pending_checkout);
  if (state.kind === "resumable") {
    redeemedCheckout.value = state.checkout;
    redirectToCheckout(state.checkout.checkout_url as string);
  }
}

async function handleErrorAction() {
  if (!redemptionError.value && previewError.value) {
    await previewQuery.refetch();
    return;
  }
  if (redemptionError.value?.action === "refresh") {
    await refreshPendingCheckout();
    return;
  }
  await startCheckout();
}

onBeforeUnmount(() => {
  if (redirectFallbackTimer) clearTimeout(redirectFallbackTimer);
  redirectFallbackTimer = null;
});
</script>

<template>
  <UiCard v-if="cardMode !== 'hidden'" padding="lg" data-testid="expert-billing-checkout-card">
    <div class="flex flex-col gap-6">
      <div class="flex items-start gap-4">
        <div class="flex size-11 shrink-0 items-center justify-center rounded-full bg-se-gray-100">
          <UiIcon
            :name="['success', 'confirmed', 'timeout'].includes(cardMode) ? 'check-circle-outline' : 'lock'"
            class="size-5 text-se-dark"
          />
        </div>

        <div class="min-w-0 flex-1">
          <UiText
            v-if="cardMode === 'confirmed'"
            as="h2"
            variant="h4"
            text="Subscription confirmed"
          />
          <UiText
            v-else-if="cardMode === 'success'"
            as="h2"
            variant="h4"
            text="Subscription submitted — confirming with Stripe"
          />
          <UiText
            v-else-if="cardMode === 'timeout'"
            as="h2"
            variant="h4"
            text="Subscription confirmation delayed"
          />
          <UiText
            v-else-if="cardMode === 'redirecting'"
            as="h2"
            variant="h4"
            text="Taking you to secure checkout"
          />
          <UiText
            v-else-if="cardMode === 'preview-loading'"
            as="h2"
            variant="h4"
            text="Review your subscription offer"
          />
          <UiText
            v-else-if="cardMode === 'resume'"
            as="h2"
            variant="h4"
            :text="returnState.kind === 'cancelled' ? 'Your checkout is ready when you are' : 'Finish setting up your subscription'"
          />
          <UiText
            v-else-if="cardMode === 'expired'"
            as="h2"
            variant="h4"
            text="Your previous checkout expired"
          />
          <UiText
            v-else-if="cardMode === 'error'"
            as="h2"
            variant="h4"
            :text="activeError?.title"
          />
          <UiText
            v-else-if="cardMode === 'role'"
            as="h2"
            variant="h4"
            text="An expert account is required"
          />
          <UiText
            v-else
            as="h2"
            variant="h4"
            text="Complete your ShopExperts subscription"
          />

          <UiText
            v-if="cardMode === 'confirmed'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Stripe confirmed your subscription. Your current plan and billing details are up to date below."
          />
          <UiText
            v-else-if="cardMode === 'success'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Stripe is confirming your subscription with ShopExperts. Your access and billing details will update only after that confirmation is complete."
          />
          <UiText
            v-else-if="cardMode === 'timeout'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Stripe is taking longer than expected to confirm your subscription. You can safely leave this page and check again later; do not pay again."
          />
          <UiText
            v-else-if="cardMode === 'role'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Sign in with the expert account that received this payment invitation."
          />
          <UiText
            v-else-if="cardMode === 'error'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            :text="activeError?.message"
          />
          <UiText
            v-else-if="cardMode === 'preview-loading'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Loading the exact plan, price, and billing schedule attached to your invitation."
          />
          <UiText
            v-else-if="cardMode === 'expired'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Stripe can no longer reopen this checkout. Use the original payment invitation again to create a fresh one."
          />
          <UiText
            v-else-if="cardMode === 'unavailable'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="We found your checkout, but its secure link is not available yet. Refresh the checkout state and try again."
          />
          <UiText
            v-else-if="cardMode === 'cancelled-loading'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Your payment was not submitted. We’re checking whether your secure checkout can be resumed."
          />
          <UiText
            v-else-if="cardMode === 'cancelled-empty'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Your payment was not submitted and there is no open checkout to resume. Open the original payment invitation to try again."
          />
          <UiText
            v-else-if="cardMode === 'pending-error'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Your payment was not submitted. We couldn’t check the previous checkout, so refresh before trying again."
          />
          <UiText
            v-else-if="cardMode === 'invite'"
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Review the exact terms below, then continue to Stripe to securely submit payment."
          />
          <UiText
            v-else
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Continue to Stripe to review the exact plan, billing schedule, and total before submitting payment."
          />
        </div>
      </div>

      <div
        v-if="offerTerms && !['success', 'confirmed', 'timeout', 'role', 'error'].includes(cardMode)"
        class="grid gap-4 border-y border-se-gray-200 py-5 sm:grid-cols-2 lg:grid-cols-4"
      >
        <div>
          <UiText as="span" variant="caption-normal" class="text-se-gray-600" text="Plan" />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 font-semibold text-se-dark"
            :text="[offerTerms.plan?.name, offerTerms.billing_option?.label].filter(Boolean).join(' — ') || 'Subscription plan'"
          />
        </div>
        <div>
          <UiText as="span" variant="caption-normal" class="text-se-gray-600" text="Price" />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 font-semibold text-se-dark"
            :text="formatCheckoutAmount(offerTerms)"
          />
        </div>
        <div>
          <UiText as="span" variant="caption-normal" class="text-se-gray-600" text="Billing schedule" />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 font-semibold text-se-dark"
            :text="formatCheckoutCadence(offerTerms)"
          />
        </div>
        <div v-if="offerExpiresAt">
          <UiText
            as="span"
            variant="caption-normal"
            class="text-se-gray-600"
            :text="cardMode === 'invite' ? 'Offer expires' : 'Checkout expires'"
          />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 font-semibold text-se-dark"
            :text="formatCheckoutDate(offerExpiresAt)"
          />
        </div>
      </div>

      <UiInfoBox
        v-if="cardMode === 'resume' && returnState.kind === 'cancelled'"
        variant="warning"
        title="Payment was not submitted"
      >
        <UiText
          as="p"
          variant="p-small"
          :text="`This secure Checkout is available until ${formatCheckoutDate(offerExpiresAt)}.`"
        />
      </UiInfoBox>

      <div v-if="cardMode === 'preview-loading'" class="flex items-center gap-3" role="status">
        <UiIcon name="refresh-cw" class="size-5 animate-spin text-se-gray-600" />
        <UiText as="p" variant="p-small" text="Loading invitation terms…" />
      </div>

      <div v-if="cardMode === 'redirecting'" class="flex items-center gap-3" role="status">
        <UiIcon name="refresh-cw" class="size-5 animate-spin text-se-gray-600" />
        <UiText as="p" variant="p-small" text="Opening Stripe Checkout…" />
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <UiButton
          v-if="cardMode === 'invite'"
          variant="primary"
          :label="redeemMutation.isPending.value ? 'Preparing checkout…' : 'Continue to secure checkout'"
          :disabled="redeemMutation.isPending.value"
          @click="startCheckout"
        >
          <template v-if="redeemMutation.isPending.value" #before_label>
            <UiIcon name="refresh-cw" class="size-4 animate-spin" />
          </template>
        </UiButton>

        <UiButton
          v-else-if="cardMode === 'resume'"
          variant="primary"
          label="Resume secure checkout"
          @click="resumeCheckout"
        />

        <UiButton
          v-else-if="cardMode === 'expired' && invitationToken"
          variant="primary"
          :label="redeemMutation.isPending.value ? 'Preparing checkout…' : 'Start a new checkout'"
          :disabled="redeemMutation.isPending.value"
          @click="startCheckout"
        />

        <UiButton
          v-else-if="cardMode === 'unavailable'"
          variant="outline"
          :label="isBusy ? 'Refreshing…' : 'Refresh checkout'"
          :disabled="isBusy"
          @click="refreshPendingCheckout"
        />

        <UiButton
          v-else-if="cardMode === 'pending-error'"
          variant="outline"
          :label="isBusy ? 'Refreshing…' : 'Refresh checkout'"
          :disabled="isBusy"
          @click="refreshPendingCheckout"
        />

        <UiButton
          v-else-if="cardMode === 'error' && activeError && activeError.action !== 'none'"
          variant="primary"
          :label="errorActionLabel"
          :disabled="isBusy || !canRetryNow"
          @click="handleErrorAction"
        />

        <UiButton
          v-if="cardMode === 'redirecting' && showRedirectFallback && redirectFallbackUrl"
          variant="outline"
          label="Open Stripe Checkout"
          :href="redirectFallbackUrl"
          target="_self"
        />
      </div>
    </div>
  </UiCard>
</template>
